// @ts-strict-ignore
import { Connection } from '@3d-scene/model3d/connection';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ISpanData } from '@project/components/label/label-editor/span-data';
import { TableTextItemVO, TableTextRowVO, TableTextValueVO } from '@shared/vo/meta-element.vo';
import { ITableProvider } from '@project/components/label/label-editor/table-provider';
import { NumberUtils } from '@shared/util/number-utils';

export class TableProviderUtils {

  /**
   * Calculates common row-count for connections which belong to the same row in label-editor.
   */
  public static calculateLineSpan(connectionCubes: Connection[]): number {
    let baserowDistance: number = 1;
    const maxMultilinePresent: number = LabelingUtils.getMaxAllowedMultilines(connectionCubes);
    if (maxMultilinePresent > 1) {
      const lines: number[] = [];
      for (let i: number = maxMultilinePresent; i > 1; i--) {
        lines.push(i);
      }
      baserowDistance = MathUtils.calculateLcmForIntArray(lines);
    }
    return baserowDistance;
  }

  public static mergeSpanDatas(target: ISpanData[][], source: ISpanData[][]): void {
    if (!source) {
      return;
    }

    for (let i: number = 0; i < source.length; i++) {
      const sourceRow: ISpanData[] = source[i];
      let targetRow: ISpanData[] = target[i] || [];
      targetRow = targetRow.concat(sourceRow);
      target[i] = targetRow;
    }
  }

  public static addSpanData(target: ISpanData[][], data: ISpanData, rowIndex: number, colIndex: number): void {
    if (!target[rowIndex]) {
      target[rowIndex] = [];
    }
    target[rowIndex][colIndex] = data;
  }

  public static addSpanDataColumn(target: ISpanData[][], column: ISpanData[]): void {
    if (!column) {
      return;
    }

    for (let i: number = 0; i < column.length; i++) {
      const targetRow: ISpanData[] = target[i] || [];
      targetRow.push(column[i]);
      target[i] = targetRow;
    }
  }

  public static insertSpanDataColumn(target: ISpanData[][], column: ISpanData[], position: number): void {
    if (!column) {
      return;
    }

    for (let i: number = 0; i < column.length; i++) {
      const targetRow: ISpanData[] = target[i] || [];
      targetRow.splice(position, 0, column[i]);
      target[i] = targetRow;
    }
  }

  public static getSpanDataColumn(source: ISpanData[][], columnIndex: number): ISpanData[] {
    const column: ISpanData[] = [];
    for (let i: number = 0; i < source.length; i++) {
      const row: ISpanData[] = source[i];
      if (row) {
        const columnItem: ISpanData = row[columnIndex];
        if (columnItem) {
          column.push(columnItem);
        }
      }
    }
    return column;
  }

  public static findRowByIndex(table: TableTextValueVO, index: number, step: number): TableTextRowVO {
    if (!table || !table.rows) {
      return null;
    }

    return table.rows[Math.floor(index / step)];
  }

  public static getRowIndex(table: TableTextValueVO, row: TableTextRowVO, step: number): number {
    return table.rows.indexOf(row) * step;
  }

  public static findRowItemByIndex(tableRow: TableTextRowVO, index: number): TableTextItemVO {
    if (!tableRow || !tableRow.items) {
      return null;
    }

    let colIndex: number = 0;
    for (const tableItem of tableRow.items) {
      if (index >= colIndex && index < colIndex + tableItem.colSpan) {
        return tableItem;
      }
      colIndex += tableItem.colSpan;
    }
    return null;
  }

  public static getRowItemIndex(tableRow: TableTextRowVO, item: TableTextItemVO): number {
    let rowIndex: number = 0;
    for (let i: number = 0; i < tableRow.items.indexOf(item); i++) {
      rowIndex += tableRow.items[i].colSpan;
    }
    return rowIndex;
  }

  public static getMergedColCount(tableProvider: ITableProvider, startRowIndex: number, lineSpan: number, startColIndex: number): number {
    let maxColCount: number = 1;
    for (let rowIndex: number = 0; rowIndex < lineSpan; rowIndex++) {
      const spanData: ISpanData = tableProvider.getSpanData(rowIndex + startRowIndex, startColIndex);
      if (!spanData.parent && spanData.colSpan > 1) {
        if (spanData.colSpan > maxColCount) {
          maxColCount = spanData.colSpan;
        }
      }
    }

    let mergedColCount: number = maxColCount;
    for (let colIndex: number = 1; colIndex < maxColCount; colIndex++) {
      const nextMaxColCount: number = this.getMergedColCount(tableProvider, startRowIndex, lineSpan, startColIndex + colIndex);
      const nextMaxColCountFromStart: number = nextMaxColCount + colIndex;
      if (nextMaxColCountFromStart > mergedColCount) {
        mergedColCount = nextMaxColCountFromStart;
      }
    }
    return mergedColCount;
  }

  public static sortTableProvidersByPosition(a: ITableProvider, b: ITableProvider): number {
    const minA: number = a.getMinPositionOnRail();
    const minB: number = b.getMinPositionOnRail();
    if (NumberUtils.equals(minA, minB)) {
      return NumberUtils.numericCompare(a.getMaxPositionOnRail(), b.getMaxPositionOnRail());
    } else {
      return NumberUtils.numericCompare(minA, minB);
    }
  }
}
